import { apiGet, apiPost, apiPatch, apiDelete } from "./client";
import type {
  AccountHealthSummary,
  CompanyActivitiesResponse,
  CompanySearchFilters,
  MergedAccountView,
} from "@sales-ops-amplify/shared";
import { getAccessToken } from "@/stores/authStore";

const API_BASE = import.meta.env.VITE_API_BASE_URL ?? "";
const USER_ACCOUNTS = "/api/user/accounts";

export interface ListAccountsParams {
  search?: string;
  industry?: string;
  country?: string;
  limit?: number;
  offset?: number;
}

export interface ListAccountsResponse {
  companies: MergedAccountView[];
  total: number;
  limit?: number;
  offset?: number;
  hasMore?: boolean;
  scope?: string;
  filters?: { industry?: string; country?: string; search?: string };
}

/** List user Account shells (merged with directory intel). */
export async function listAccounts(
  params?: ListAccountsParams,
): Promise<ListAccountsResponse> {
  return apiGet<ListAccountsResponse>(USER_ACCOUNTS, {
    ...(params?.search ? { search: params.search } : {}),
    ...(params?.industry ? { industry: params.industry } : {}),
    ...(params?.country ? { country: params.country } : {}),
    ...(params?.limit != null ? { limit: params.limit } : {}),
    ...(params?.offset != null ? { offset: params.offset } : {}),
  });
}

/** Fetch merged Account detail (directory + user shell). */
export async function getAccount(accountId: string): Promise<MergedAccountView> {
  return apiGet<MergedAccountView>(`${USER_ACCOUNTS}/${accountId}`);
}

export async function updateAccount(
  accountId: string,
  updates: Record<string, unknown>,
): Promise<void> {
  await apiPatch(`${USER_ACCOUNTS}/${accountId}`, updates);
}

export async function researchAccount(
  accountId: string,
): Promise<{ message: string; research: string }> {
  return apiPost(`${USER_ACCOUNTS}/${accountId}/research`);
}

export async function enrichAccount(
  accountId: string,
): Promise<{ message: string; fields?: Record<string, unknown> }> {
  return apiPost(`${USER_ACCOUNTS}/${accountId}/enrich`);
}

export async function getAccountHealth(
  accountId: string,
): Promise<AccountHealthSummary> {
  return apiGet(`${USER_ACCOUNTS}/${accountId}/health`);
}

export function getAccountActivities(
  accountId: string,
  params?: { types?: string[]; limit?: number },
): Promise<CompanyActivitiesResponse> {
  const q = new URLSearchParams();
  if (params?.types?.length) q.set("types", params.types.join(","));
  if (params?.limit != null) q.set("limit", String(params.limit));
  const qs = q.toString();
  return apiGet(
    `${USER_ACCOUNTS}/${encodeURIComponent(accountId)}/activities${qs ? `?${qs}` : ""}`,
  );
}

export async function deleteAccount(
  accountId: string,
): Promise<{ message: string; accountId: string }> {
  return apiDelete(`${USER_ACCOUNTS}/${encodeURIComponent(accountId)}`);
}

/** Soft-delete Accounts by slug and/or all matching filters. */
export async function bulkDeleteAccounts(body: {
  ids?: string[];
  filters?: CompanySearchFilters;
  allMatching?: boolean;
}): Promise<{ deleted: number }> {
  return apiPost(`${USER_ACCOUNTS}/bulk-delete`, body);
}

export async function restoreAccount(
  accountId: string,
): Promise<{ message: string; accountId: string }> {
  return apiPost(`${USER_ACCOUNTS}/${encodeURIComponent(accountId)}/restore`, {});
}

export async function purgeAccount(
  accountId: string,
): Promise<{ message: string; accountId: string }> {
  return apiPost(`${USER_ACCOUNTS}/${encodeURIComponent(accountId)}/purge`, {});
}

export async function purgeDeletedAccounts(body?: {
  slugs?: string[];
}): Promise<{ purged: number }> {
  return apiPost(`${USER_ACCOUNTS}/purge-deleted`, body ?? {});
}

/** Export user-scoped Accounts as full-field CSV. */
export async function exportAccountsCsv(slugs?: string[]): Promise<Blob> {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };
  const token = getAccessToken();
  if (token) headers.Authorization = `Bearer ${token}`;

  const res = await fetch(`${API_BASE}${USER_ACCOUNTS}/export`, {
    method: "POST",
    headers,
    body: JSON.stringify(slugs?.length ? { slugs } : {}),
  });
  if (!res.ok) {
    const errBody = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(
      typeof errBody === "object" && errBody && "error" in errBody
        ? String((errBody as { error: string }).error)
        : "Export failed",
    );
  }
  return res.blob();
}
